/**
 * `Editor - Open Menu`: one rule for every menu surface — report the closed → open edge and
 * nothing else. A menu that merely appears under the pointer (the link hover menu) never gets
 * here at all; a mouse crossing a lesson's links would blow the whole menu budget.
 */

import type { Editor } from "@tiptap/core"
import { getCardId, getContainingCardId } from "../util/card"
import { guardAnalytics } from "./instrument"
import type { CardId, HostId } from "./types"

/**
 * An editor open-menu analytics event - the object passed to an AnalyticsRuntime's EditorAnalyticsPort.
 *
 * It describes context that the AnalyticsRuntime turns into an action narrative.
 */
export interface EditorOpenMenuEvent {
  readonly type: "open-menu"
  readonly surface: MenuSurface
  readonly cardId: CardId | null
  readonly hostId: HostId
  readonly occurredAt: string
}

export type MenuSurface =
  | "card-menu"
  | "lesson-menu"
  | "add-button"
  | "comment-button"
  | "link-popover"
  | "google-classroom-sidebar"

/**
 * `cardId` is a thunk so a stale anchor is never resolved on the close edge, and it runs inside
 * the failure boundary — it resolves a document position, and this is called from react-aria's
 * `onOpenChange`, where a throw would stop the menu opening.
 */
export function reportMenuOpenChange(
  editor: Editor,
  surface: MenuSurface,
  isOpen: boolean,
  cardId: () => CardId | null,
): void {
  if (!isOpen) return
  guardAnalytics(editor, (runtime) => {
    const event: EditorOpenMenuEvent = {
      type: "open-menu",
      surface,
      cardId: cardId(),
      hostId: runtime.hostId,
      occurredAt: new Date().toISOString(),
    }
    runtime.port.recordOpenMenu(event)
  })
}

/**
 * The card menu's open edge, for the one surface with two implementations (a normal card's title
 * buttons and the Google Classroom card's). Both hand over their node view's `getPos()`.
 */
export function reportCardMenuOpenChange(
  editor: Editor,
  args: { getPos: () => number | null; isOpen: boolean },
): void {
  reportMenuOpenChange(editor, "card-menu", args.isOpen, () =>
    cardIdForMenuAnchor(editor, args.getPos()),
  )
}

/**
 * The card a menu anchored at `pos` acts on. Two anchor shapes: a position inside the card, and a
 * position AT the card node itself — the Google Classroom card is an atom, so its buttons anchor
 * on the card's own position, which resolves to the document rather than into the card.
 */
export function cardIdForMenuAnchor(editor: Editor, pos: number | null): CardId | null {
  if (pos === null) return null
  const { doc } = editor.state
  const inside = getContainingCardId(doc, pos)
  if (inside !== null) return inside
  // A node view's getPos() can be stale (its node was removed while the menu was up), and nodeAt
  // throws a RangeError outside the document — so clamp, the way getContainingCardId already
  // clamps for itself.
  const clamped = Math.max(0, Math.min(pos, doc.content.size))
  return getCardId(doc.nodeAt(clamped))
}
